import { Directive, ElementRef, Renderer2 } from '@angular/core';
import { ResizeObserver } from '@juggle/resize-observer';
import { BaseDirective } from '../../../../../../../models/base/base-directive';

/**
 * Hides the children of a wrapping flex container that fall below its first line.
 * Clipping the wrapped lines alone leaves the container at the full available width, so the visible children sit
 * off-centre; removing them from layout lets the container hug the children that fit.
 */
@Directive({
  selector: '[appTruncateToFirstLine]',
  standalone: false
})
export class TruncateToFirstLineDirective extends BaseDirective {
  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {
    super();
  }

  private resizeObserver: ResizeObserver;
  private childListObserver: MutationObserver;

  setupViews() {}

  setupBindings() {
    // Children are observed too: badge images only gain their width once loaded.
    this.resizeObserver = new ResizeObserver(this.hideChildrenBelowFirstLine);
    this.childListObserver = new MutationObserver(this.observeContainerAndChildren);
    this.childListObserver.observe(this.elementRef.nativeElement, { childList: true });
    this.observeContainerAndChildren();
  }

  private observeContainerAndChildren = (): void => {
    const container = this.elementRef.nativeElement;
    this.resizeObserver.disconnect();
    this.resizeObserver.observe(container);
    Array.from(container.children).forEach(child => this.resizeObserver.observe(child));
  };

  // Unhide and re-hide in one pass so the final sizes match the last observation and the observer settles.
  private hideChildrenBelowFirstLine = (): void => {
    const children = Array.from(this.elementRef.nativeElement.children) as HTMLElement[];
    children.forEach(child => this.renderer.removeStyle(child, 'display'));
    const firstChild = children[0];
    const firstLineBottom = firstChild?.offsetTop + firstChild?.offsetHeight;
    const wrappedChildren = children.filter(child => child.offsetTop >= firstLineBottom);
    wrappedChildren.forEach(child => this.renderer.setStyle(child, 'display', 'none'));
  };

  destroy() {
    super.destroy();
    this.resizeObserver?.disconnect();
    this.childListObserver?.disconnect();
  }
}
